import { ICellValue, ITableRow, ITableViewColour, ITableViewColumn, IRowLevelColour } from 'types'
import { doesFilterMatchValue } from 'components/spreadsheet/helpers/filtering'
import { IColumnValuesCount } from '../types'
import { getRowValue } from './functions'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'
import { recordListsEqual } from 'components/spreadsheet/helpers/record-equality'

// Value-based comparison of two lists of colour rules, insensitive to object key
// order and to whether the optional value / multipleValues fields are present.
// The colour menu rebuilds rule objects with a different key order and omits one
// of value/multipleValues; comparing those with JSON.stringify made the save icon
// flag "unsaved changes" when a view with existing colour rules was merely opened
// (#10120). recordListsEqual compares the union of keys, so a new attribute added
// to ITableViewColour is picked up automatically without editing this helper.
// A hovered row's cells: lighter than the grey around the table (surface-sunken),
// which grouped and read-only cells use, so hovering never reads as a gap.
export const ROW_HOVER_FILL = 'var(--color-surface-muted)'

export const areColourSettingsEqual = (a: ITableViewColour[], b: ITableViewColour[]): boolean => {
  return recordListsEqual(a as unknown as Record<string, unknown>[], b as unknown as Record<string, unknown>[])
}

export const getRowLevelColour = (
  colourSettings: ITableViewColour[],
  row: ITableRow,
  columnLookup: Map<string, ITableViewColumn>,
  columnIdToIndex: Map<string, number>,
  columnValuesCount: IColumnValuesCount,
  greyBackground?: boolean,
  defaultColour?: string,
  columnStore?: CompressedColumnStore | null
): IRowLevelColour => {
  let backgroundColour = greyBackground
    ? 'var(--color-surface-sunken)'
    : defaultColour
      ? defaultColour
      : 'var(--color-surface)'
  let fontColour = 'var(--color-body)'
  let fontBold = false
  let fontItalic = false
  let fontUnderline = false
  let matchedRuleIndex = -1

  for (let i = 0; i < colourSettings.length; i++) {
    const colourSetting = colourSettings[i]
    if (colourSetting.applyToEntireRow) {
      // O(1) Map lookup instead of O(n) array.find()
      const ruleColumn = columnLookup.get(colourSetting.columnId)
      if (ruleColumn) {
        const ruleColumnValue = getRowValue(row, colourSetting.columnId, columnIdToIndex, columnStore)
        const match = doesFilterMatchValue(
          colourSetting.filterType,
          colourSetting.value,
          colourSetting.multipleValues,
          ruleColumnValue,
          '',
          '',
          [],
          [],
          [],
          [],
          ruleColumn,
          columnValuesCount,
          row
        )
        if (match) {
          backgroundColour = colourSetting.backgroundColour
          fontColour = colourSetting.fontColour
          fontBold = colourSetting.fontBold
          fontItalic = colourSetting.fontItalic
          fontUnderline = colourSetting.fontUnderline
          matchedRuleIndex = i
          break
        }
      }
    }
  }

  return {
    backgroundColour,
    fontColour,
    fontBold,
    fontItalic,
    fontUnderline,
    matchedRuleIndex
  }
}

export const getCellColour = (
  column: ITableViewColumn,
  columnValuesCount: IColumnValuesCount,
  row: ITableRow | null,
  value?: ICellValue,
  colourSettings?: ITableViewColour[],
  greyBackground?: boolean,
  defaultColour?: string,
  rowLevelColour?: IRowLevelColour | null,
  rowData?: { [columnId: string]: ICellValue } | null
) => {
  let backgroundColour = greyBackground
    ? 'var(--color-surface-sunken)'
    : defaultColour
      ? defaultColour
      : 'var(--color-surface)'
  let fontColour = 'var(--color-body)'
  let fontBold = false
  let fontItalic = false
  let fontUnderline = false

  // If row-level colors were pre-calculated, use them as the base
  if (rowLevelColour && rowLevelColour.matchedRuleIndex >= 0) {
    backgroundColour = rowLevelColour.backgroundColour
    fontColour = rowLevelColour.fontColour
    fontBold = rowLevelColour.fontBold
    fontItalic = rowLevelColour.fontItalic
    fontUnderline = rowLevelColour.fontUnderline
  }

  if (column && colourSettings && value !== undefined) {
    for (let i = 0; i < colourSettings.length; i++) {
      const colourSetting = colourSettings[i]

      // If row-level colors were pre-calculated, skip row-level rules
      if (rowLevelColour && colourSetting.applyToEntireRow) {
        continue
      }

      // If a row-level rule matched, only apply cell-specific rules that come before it
      if (rowLevelColour && rowLevelColour.matchedRuleIndex >= 0 && i >= rowLevelColour.matchedRuleIndex) {
        break
      }

      if (
        (colourSetting.columnId === column.publicId ||
          (row?.originalColumnId && colourSetting.columnId === row.originalColumnId)) &&
        value !== undefined
      ) {
        // Standard column-specific rule - only applies if it comes before any matched row-level rule
        const match = doesFilterMatchValue(
          colourSetting.filterType,
          colourSetting.value,
          colourSetting.multipleValues,
          value,
          '',
          '',
          [],
          [],
          [],
          [],
          column,
          columnValuesCount,
          row,
          undefined,
          undefined,
          rowData
        )
        if (match) {
          backgroundColour = colourSetting.backgroundColour
          fontColour = colourSetting.fontColour
          fontBold = colourSetting.fontBold
          fontItalic = colourSetting.fontItalic
          fontUnderline = colourSetting.fontUnderline
          break
        }
      }
    }
  }
  return {
    backgroundColour,
    fontColour,
    fontBold,
    fontItalic,
    fontUnderline
  }
}
